import Form from '@rjsf/core';
import { Model } from '@sgdocs/client';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { computeSchema, computeUiSchema, JsonSchemaWidgets } from '../../common/json-schema/JsonSchemaForm';
import { DateRangeJsonSchemaWidget } from '../../common/json-schema/widgets/DateRangeJsonSchemaWidget';

interface ExtraSearch {
  inputSearch: string;
  creationDate: string;
}

export type FiltersData = {
  extraSearch: ExtraSearch;
  modelFilterData: any;
};

interface ISGMDocsSearchFromModelLayout {
  searchValue: string;
  model: Model;
  initFilterFormData?: any;
  onSubmit(formData: FiltersData): void;
  labels: {
    inputPlaceholder: string;
    hideFilters: string;
    showFilters: string;
    search: string;
  };
}

interface IButtonProps {
  handleClick: (event: any) => void;
  color?: string;
  discreet?: boolean;
  iconLabel?: string;
  additionalClassNames?: string;
}

const Button = ({
  handleClick,
  color = 'primary',
  discreet,
  iconLabel,
  children,
  additionalClassNames = '',
}: PropsWithChildren<IButtonProps>): JSX.Element => {
  return (
    <button
      className={`btn btn-${discreet ? 'discreet-' : ''}${color} btn-lg ${
        iconLabel ? 'btn-icon-start' : ''
      } ${additionalClassNames}`}
      onClick={(event): void => {
        event.preventDefault();
        handleClick(event);
      }}
    >
      {iconLabel && <i className={`icon icon-md ${children ? 'me-1' : ''}`}>{iconLabel}</i>}
      {children}
    </button>
  );
};

export const SGMDocsSearchFromModelLayout = ({
  searchValue,
  model,
  initFilterFormData,
  onSubmit,
  labels,
}: ISGMDocsSearchFromModelLayout): JSX.Element => {
  const [formData, setFormData] = useState();
  const [extraSearch, setExtraSearch] = useState<ExtraSearch>({
    inputSearch: searchValue || '',
    creationDate: '',
  });
  const [showAdditionalFilters, setShowAdditionalFilters] = useState<boolean>(false);
  const [additionalFiltersTouched, setAdditionalFiltersTouched] = useState<boolean>(false);

  const enterToSubmit = (event: any): void => {
    if (event.keyCode === 13) {
      handleSubmit();
    }
  };
  const schema = computeSchema(model.jsonSchema, true);
  const uiSchema = computeUiSchema(schema, 3, true);

  useEffect(() => {
    setFormData(initFilterFormData);
    onSubmit({ extraSearch, modelFilterData: initFilterFormData });
  }, [initFilterFormData]);

  const handleSubmit = (): void => {
    onSubmit({
      extraSearch,
      modelFilterData: additionalFiltersTouched ? formData : initFilterFormData,
    });
  };
  const submitForm = (): void => {
    document.getElementById('search-document-button')?.click();
  };

  return (
    <>
      <div className="row mb-2">
        <div className={'col-3 px-0'}>
          <div className="form-group">
            <div className="input-group input-group-lg">
              <div className="input-icon-start">
                <i className="icon">search</i>
              </div>
              <input
                type="search"
                id="docs-search-bar"
                className="form-control form-control-lg"
                placeholder={labels.inputPlaceholder}
                defaultValue={extraSearch.inputSearch || ''}
                onChange={(e): void => setExtraSearch((prev) => ({ ...prev, inputSearch: e.target.value }))}
                onKeyDown={enterToSubmit}
                autoComplete="off"
              />
            </div>
          </div>
        </div>
        <div className={'col-9'}>
          <Button
            iconLabel={'add'}
            color={'outline-primary'}
            handleClick={(): void => {
              setAdditionalFiltersTouched(true);
              setShowAdditionalFilters((currentShow) => !currentShow);
            }}
          >
            {showAdditionalFilters ? labels.hideFilters : labels.showFilters}
          </Button>
          <Button iconLabel={'search'} additionalClassNames={'ms-4'} handleClick={submitForm}>
            {labels.search}
          </Button>
        </div>
      </div>
      <div className={`container px-0 ${showAdditionalFilters ? '' : 'd-none'}`}>
        <div className="py-4 border-bottom">
          <Form
            idPrefix={'add-document'}
            name={'add-document-form'}
            schema={schema}
            uiSchema={uiSchema}
            widgets={JsonSchemaWidgets}
            formData={formData}
            onChange={(event): void => setFormData(event.formData)}
            noHtml5Validate
            noValidate
            onSubmit={handleSubmit}
          >
            <button id="search-document-button" type="submit" style={{ display: 'none' }} />
          </Form>
          <div className="row">
            <div className="form-group col-md-4">
              <label className="col-form-label mb-2" htmlFor="documents-creation-date-search">
                Creation Date
              </label>
              <div id="documents-creation-date-search">
                <DateRangeJsonSchemaWidget
                  value={extraSearch.creationDate}
                  onChange={(d): void => setExtraSearch((prev) => ({ ...prev, creationDate: d }))}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
